import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Form, Table, Row, Col } from "react-bootstrap";
import Swal from "sweetalert2";
import api from "../utils/api";
import "./VolumePricing.css";

const VolumePricing = () => {
  const [volumePricings, setVolumePricings] = useState([]);
  const [products, setProducts] = useState([]);
  const [show, setShow] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [formData, setFormData] = useState({
    product_id: "",
    quantity: "",
    price_per_item: "",
    discount: "",
  });

  useEffect(() => {
    fetchVolumePricings();
    fetchProducts();
  }, []);

  const fetchVolumePricings = async () => {
    try {
      const response = await axios.get(api.getUrl("volume_pricing.php"));
      if (response.data.success) {
        // Fetch all products to get images
        const productsResponse = await axios.get(api.getUrl("products.php"));
        const productsMap = {};
        if (productsResponse.data) {
          productsResponse.data.forEach(product => {
            productsMap[product.id] = product;
          });
        }

        // Match product images from the products list
        const rulesWithImages = response.data.rules.map(rule => {
          const product = productsMap[rule.product_id];
          return {
            ...rule,
            product_image: product && product.images && product.images.length > 0 ? product.images[0] : null
          };
        });
        setVolumePricings(rulesWithImages);
      }
    } catch (error) {
      console.error("Error fetching volume pricings:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(api.getUrl("products.php"));
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleShow = (pricing = null) => {
    if (pricing) {
      setEditingPricing(pricing);
      setFormData({
        product_id: pricing.product_id,
        quantity: pricing.quantity,
        price_per_item: pricing.price_per_item,
        discount: pricing.discount,
      });
    } else {
      setEditingPricing(null);
      setFormData({
        product_id: "",
        quantity: "",
        price_per_item: "",
        discount: "",
      });
    }
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setEditingPricing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      product_id: parseInt(formData.product_id),
      quantity: parseInt(formData.quantity),
      price_per_item: parseFloat(formData.price_per_item),
      discount: parseFloat(formData.discount),
    };

    try {
      if (editingPricing) {
        data.id = editingPricing.id;
        await axios.put(api.getUrl("volume_pricing.php"), data);
        Swal.fire("Success!", "Volume pricing updated successfully.", "success");
      } else {
        await axios.post(api.getUrl("volume_pricing.php"), data);
        Swal.fire("Success!", "Volume pricing added successfully.", "success");
      }
      fetchVolumePricings();
      handleClose();
    } catch (error) {
      console.error("Error saving volume pricing:", error);
      Swal.fire("Error!", "Failed to save volume pricing.", "error");
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This volume pricing rule will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff6b00",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(api.getUrl("volume_pricing.php"), {
            data: { id }
          });
          Swal.fire("Deleted!", "The volume pricing rule has been removed.", "success");
          fetchVolumePricings();
        } catch (error) {
          Swal.fire("Error!", "Failed to delete volume pricing rule.", "error");
        }
      }
    });
  };

  return (
    <div className="volume-pricing-container">
      <h2>Manage Volume Pricing</h2>
      <Button variant="primary" onClick={() => handleShow()}>Add Volume Pricing</Button>

      <Table striped bordered hover className="mt-3">
        <thead>
          <tr>
            <th>Product</th>
            <th>Quantity</th>
            <th>Price per Item</th>
            <th>Discount (%)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {volumePricings.map((pricing) => (
            <tr key={pricing.id}>
              <td>
                <div className="d-flex align-items-center">
                  {pricing.product_image && (
                    <img
                      src={api.getImageUrl(pricing.product_image)}
                      alt={pricing.product_name}
                      style={{ width: '50px', height: '50px', objectFit: 'cover', marginRight: '10px', borderRadius: '4px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <span>{pricing.product_name}</span>
                </div>
              </td>
              <td>{pricing.quantity}</td>
              <td>Rs.{pricing.price_per_item}</td>
              <td>{pricing.discount}%</td>
              <td>
                <Button variant="warning" size="sm" onClick={() => handleShow(pricing)}>
                  Edit
                </Button>{" "}
                <Button variant="danger" size="sm" onClick={() => handleDelete(pricing.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add/Edit Modal */}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editingPricing ? "Edit Volume Pricing" : "Add Volume Pricing"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Product*</Form.Label>
              <Form.Select
                value={formData.product_id}
                onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                required
              >
                <option value="">Select Product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Quantity*</Form.Label>
              <Form.Control
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Price per Item*</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formData.price_per_item}
                onChange={(e) => setFormData({ ...formData, price_per_item: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Discount (%)*</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleClose} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingPricing ? "Update" : "Save"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default VolumePricing;
